import {
  Body,
  Controller,
  Stack,
  useForm,
  useFormContext,
  z,
} from '@qred/components-library';
import { ComboboxData } from '@qred/components-library/dist/lib/next/components/Combobox/Combobox.types';
import { OptionsFilter } from '@qred/components-library/dist/lib/next/components/Combobox/components/OptionsDropdown/OptionsDropdown';
import { FilterOptionsInput } from '@qred/components-library/dist/lib/next/components/Combobox/components/OptionsDropdown/defaultOptionsFilter';
import { AsyncSelect, SelectProps } from '@qred/components-library/next';
import { validateOrgNumber } from '@qred/shared-component-library/src/validators';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { getMarketCanSearchCompanyByName } from '~/constants/markets';
import { logToSentry } from '~/helpers/loggers.helper';
import { normalizeOrgNumberHelper } from '~/helpers/normalizeOrgNumber.helper';
import { areSameCompanies } from '~/helpers/utils';
import useTranslate from '~/hooks/useTranslate';
import { ICompanySearchResult } from '~/interfaces/shared';
import { apiGetCompanyInfoBySearch } from '~/services/api';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { setApiStatus } from '~/store/slices/loanApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import { StartApplicationForm } from '../StartApplication.schema';

const renderSelectOption: SelectProps['renderOption'] = ({ option }) => (
  <Stack spacing="xs">
    <Body size="lg" color="textDark.500">
      {option.label}
    </Body>
    <Body size="lg" color="textDark.200">
      {option.value}
    </Body>
  </Stack>
);

const companySearchSchema = z.object({
  companySearch: z
    .string()
    .trim()
    .min(1, { message: 'ValidationErrors.Required' }),
  selectedCompany: z
    .string()
    .refine((value) => value.length, {
      message: 'ValidationErrors.Required',
    })
    .nullable(),
});

type CompanySearchForm = z.infer<typeof companySearchSchema>;

export const CompanySearch = () => {
  const {
    setValue: setStartApplicationFormValue,
  } = useFormContext<StartApplicationForm>();

  const t = useTranslate();
  const dispatch = useAppDispatch();

  const { market } = useSelector((state: RootState) => state.intl);
  const {
    form,
    apiStatus: { companySearch: companySearchApiStatus },
  } = useSelector((state: RootState) => state.loanApplication);

  const formMethods = useForm<CompanySearchForm>({
    schema: companySearchSchema,
    defaultValues: {
      companySearch: form.companyName ?? '',
      selectedCompany:
        form.organizationNumber ||
        form.creditReportProvider?.creditReportProviderNumber ||
        form.connectId ||
        null,
    },
    mode: 'onTouched',
  });

  const {
    control,
    setValue,
    formState: { errors },
  } = formMethods;

  const [requestHasBeenMade, setRequestHasBeenMade] = useState(false);

  const initialCompanies =
    form.companyName &&
    (form.organizationNumber ||
      form.creditReportProvider?.creditReportProviderNumber ||
      form.connectId)
      ? [
          {
            companyName: form.companyName,
            companyNumber: form.organizationNumber || '',
            companyType: form.companyType,
            connectId: form.connectId,
            creditReportProvider: form.creditReportProvider,
          },
        ]
      : [];

  const [companies, setCompanies] = useState<ICompanySearchResult[]>(
    initialCompanies
  );
  const marketCanSearchByName = getMarketCanSearchCompanyByName(market);

  const debouncedCompanyFetch = useCallback(
    debounce(
      async (inputValue: string, callback: (data: ComboboxData) => void) => {
        dispatch(setApiStatus({ companySearch: ApiStatus.Started }));
        try {
          const result = await apiGetCompanyInfoBySearch(inputValue, market);

          setCompanies(
            result.map((company) => ({
              companyName: company.companyName,
              companyNumber: company.companyNumber ?? '',
              companyType: company.companyType,
            }))
          );

          callback(
            result.map((company) => ({
              label: company.companyName ?? '',
              value:
                company.companyNumber ||
                company.creditReportProvider?.creditReportProviderNumber ||
                '',
            }))
          );

          setRequestHasBeenMade(true);

          dispatch(setApiStatus({ companySearch: ApiStatus.Completed }));
        } catch (error: any) {
          setRequestHasBeenMade(true);
          if (error?.response?.status !== 404) {
            logToSentry(error, 'apiGetCompanyInfoBySearch');
          }
          dispatch(setApiStatus({ companySearch: ApiStatus.Failed }));
          setCompanies([]);
          callback([]);
        }
      },
      1000
    ),
    []
  );

  const clearSelectedCompany = (shouldValidate = true) => {
    setValue('selectedCompany', null);
    setValue('companySearch', '');
    setCompanies([]);
    setRequestHasBeenMade(false);

    setStartApplicationFormValue('companyDetails', undefined, {
      shouldValidate,
    });

    dispatch(
      pushToGtmLoanAction({
        actionName: 'other_company_search_result_cleared',
      })
    );
  };

  const handleCompanySelect = (
    companyIdentifier: string,
    searchValue: string
  ) => {
    const selectedCompany = companies.find(
      (company) =>
        company.companyNumber === companyIdentifier ||
        company.creditReportProvider?.creditReportProviderNumber ===
          companyIdentifier
    );

    if (!selectedCompany) {
      // TODO: Better way to do this?
      logToSentry(
        new Error(
          'No matching company found from API call to selected company'
        ),
        'companySearch'
      );
      return;
    }

    const searchedByOrgNr = areSameCompanies(
      selectedCompany.companyNumber,
      searchValue
    );

    dispatch(
      pushToGtmLoanAction({
        actionName: searchedByOrgNr
          ? 'other_company_search_result_selected_by_company_nr_search'
          : 'other_company_search_result_selected_by_company_name_search',
      })
    );

    setStartApplicationFormValue(
      'companyDetails',
      {
        companyName: selectedCompany.companyName,
        companyNumber: selectedCompany.companyNumber,
        companyType: selectedCompany.companyType,
      },
      { shouldValidate: true }
    );

    const selectedIdentifier =
      selectedCompany.companyNumber ||
      selectedCompany.creditReportProvider?.creditReportProviderNumber ||
      '';

    setValue('selectedCompany', selectedIdentifier);
    setValue('companySearch', selectedCompany.companyName, {
      shouldValidate: true,
    });

    setRequestHasBeenMade(false);
  };

  const getEmptyDropdownText = () => {
    if (companySearchApiStatus === ApiStatus.Started)
      return t('Loading') as string;

    return requestHasBeenMade && companySearchApiStatus !== ApiStatus.Idle
      ? (t('Onboarding.SelectCompanyNoSearchResultFound') as string)
      : (t('Onboarding.SelectCompanySearchValueMissingPrompt') as string);
  };

  const onBlur = (field: ControllerRenderProps) => {
    dispatch(
      pushToGtmLoanAction({
        actionName: `company_selector_${field.name}_change`,
      })
    );
    if (!field.value) {
      clearSelectedCompany();
    }
    field.onBlur();
  };

  const optionsFilter: OptionsFilter = (input: FilterOptionsInput) => {
    return input.options;
  };

  const loadData = (
    inputValue: string,
    callback: (data: ComboboxData) => void
  ) => {
    setValue('companySearch', inputValue);
    if (inputValue.trim().length < 2) {
      setRequestHasBeenMade(false);
      clearSelectedCompany(false);
      return callback([]);
    }

    const normalizedInputValue = normalizeOrgNumberHelper(inputValue, market);
    const isValidOrgNumberFormat = validateOrgNumber(
      normalizedInputValue,
      market
    );

    const searchValue = isValidOrgNumberFormat
      ? normalizedInputValue
      : inputValue;

    if (!marketCanSearchByName) {
      if (isValidOrgNumberFormat) {
        debouncedCompanyFetch(searchValue, callback);
      } else if (companies.length) {
        callback([
          {
            label: companies[0].companyName,
            value:
              companies[0].companyNumber ||
              companies[0].creditReportProvider?.creditReportProviderNumber ||
              '',
          },
        ]);
      } else {
        callback([]);
      }
    } else {
      debouncedCompanyFetch(searchValue, callback);
    }
  };

  useEffect(() => {
    return () => {
      debouncedCompanyFetch.cancel();
    };
  }, [debouncedCompanyFetch]);

  return (
    <Controller
      name="companySearch"
      control={control}
      render={({ field }) => (
        <AsyncSelect
          label={t('Onboarding.SelectCompanySearchInputLabel')}
          defaultSearchValue={field.value}
          defaultValue={
            form.organizationNumber ||
            form.creditReportProvider?.creditReportProviderNumber
          }
          defaultData={companies.map((company) => ({
            label: company.companyName,
            value:
              company.companyNumber ||
              company.creditReportProvider?.creditReportProviderNumber ||
              '',
          }))}
          filter={optionsFilter}
          loadData={loadData}
          onChange={(value) => {
            if (value) {
              handleCompanySelect(value, field.value);
            }
          }}
          error={
            errors.companySearch?.message && t(errors.companySearch?.message)
          }
          renderOption={renderSelectOption}
          onBlur={() => onBlur(field)}
          nothingFoundMessage={getEmptyDropdownText()}
          searchable={true}
          clearable={true}
          // If we don't have allowDeselect set to false, if they change the search
          // and click on the same result, we actually deselect it
          allowDeselect={false}
          onClear={clearSelectedCompany}
        />
      )}
    />
  );
};
